
import { useState } from "react";
import { cn } from "@/lib/utils";

export interface ImageGalleryProps {
  mainImage: string;
  images: string[];
  fallbackImage?: string;
  compact?: boolean;
}

export const ImageGallery = ({ 
  mainImage, 
  images = [], 
  fallbackImage = "/placeholder-image.png",
  compact = false
}: ImageGalleryProps) => {
  const [currentImage, setCurrentImage] = useState<string>(mainImage || (images.length > 0 ? images[0] : fallbackImage));
  
  // Combine mainImage and images, ensuring no duplicates
  const allImages = [mainImage, ...images.filter(img => img !== mainImage)].filter(Boolean);
  
  // If no images are provided, use fallbackImage
  if (allImages.length === 0) {
    allImages.push(fallbackImage);
  }

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    e.currentTarget.src = fallbackImage;
  };

  const aspectRatio = compact ? "aspect-[4/3]" : "aspect-video";
  const thumbnailHeight = compact ? "h-12" : "h-16";
  const thumbnailContainerClasses = compact ? "gap-1 mt-1" : "gap-4 mt-4";

  return (
    <div>
      {/* Main image */}
      <div className={cn("w-full overflow-hidden rounded-md", aspectRatio)}>
        <img
          src={currentImage}
          alt="Product main image"
          onError={handleImageError}
          className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
        />
      </div>
      
      {/* Image thumbnails */}
      {allImages.length > 1 && (
        <div className={cn("grid grid-cols-5", thumbnailContainerClasses)}>
          {allImages.slice(0, 5).map((image, index) => (
            <div 
              key={index}
              className={cn(
                thumbnailHeight,
                "aspect-square overflow-hidden rounded-md cursor-pointer border-2 transition-all",
                currentImage === image ? "border-primary" : "border-transparent hover:border-primary/50"
              )}
              onClick={() => setCurrentImage(image)}
            >
              <img
                src={image}
                alt={`Thumbnail ${index + 1}`}
                onError={handleImageError}
                className="w-full h-full object-cover"
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
